import {objectToQueryString, StringMap} from "./utils";

/**
 * Previously preloaded links that have been requested by `preloadLink()`.
 */
let preloadedLinks: Record<string, Promise<void>> = {};

/**
 * Asynchronously loads a link keeping track of which links have already
 * requested and loaded.
 *
 * Multiple requests to the same resource will return the same promise.
 *
 * @param {Object} options - used to set the link url, query parameters, link attributes and if the link should be overridden.
 * @return {Promise<void>} returns a promise to indicate if the link was successfully loaded.
 */
export function preloadLink(options: { url: string; params?: Record<string, string>; as: string; crossOrigin?: string; type?: string; override?: boolean }): Promise<void> {
    const {url, params, as, crossOrigin, override} = options;
    if (!url || url.length === 0) {
        throw new Error("Invalid url.");
    }
    if (typeof params !== "undefined" && typeof params !== "object") {
        throw new Error("Expected params to be an object.");
    }

    const src = `${url}?${objectToQueryString(params)}`;
    const existing = preloadedLinks[src];
    if (existing) {
        if (override) {
            const link = getLinkElement(src);
            if (link) {
                delete preloadedLinks[src];
                link.remove();
            }
        } else {
            return existing
        }
    }

    const promise = new Promise<void>((resolve, reject) => {
        if (findLink(src)) {
            resolve();
        }

        const link = createLinkElement(url, params, as, crossOrigin);

        const onLinkLoad = (): void => {
            resolve();
        };

        const onLinkError = (): void => {
            cleanup();
            delete preloadedLinks[src];
            link.remove();

            const defaultError = new Error(`The link "${src}" failed to preload.`);
            reject(defaultError);
        };

        link.addEventListener('load', onLinkLoad);
        link.addEventListener('error', onLinkError);

        document.head.insertBefore(link, document.head.firstElementChild);

        function cleanup(): void {
            link.removeEventListener('load', onLinkLoad);
            link.removeEventListener('error', onLinkError);
        }
    });

    preloadedLinks[src] = promise;

    return promise;
}


/**
 * Find link preload by src in the DOM
 * @param {string} src - used to set the link url and attributes.
 * @return {boolean | null} returns `true` if link exist otherwise `null`.
 */
function findLink(src: string): boolean | null {
    const currentLink: HTMLLinkElement | null = document.querySelector(`link[rel="preload"][href^="${src}"]`);
    if (currentLink === null) {
        return null
    }
    return true;
}

/**
 * Get link preload by src in the DOM
 * @param {string} src - used to set the link url and attributes.
 * @return {HTMLLinkElement | null} returns existing `HTMLLinkElement` if link exist otherwise `null`.
 */
function getLinkElement(src: string): HTMLLinkElement | null {
    const currentLink: HTMLLinkElement | null = document.querySelector(`link[rel="preload"][href^="${src}"]`);
    if (currentLink === null) {
        return null
    }
    return currentLink;
}

/**
 * Creates link Element
 * @param {string} url - the link url
 * @param {StringMap} params - parameters to append to link url
 * @param {string} as - parameters to append to link attributes
 * @param {string | null} [crossOrigin] - parameters to append to link attributes
 * @param {string} [type] - parameters to append to link attributes
 * @return {HTMLLinkElement} returns the newly created link.
 */
export function createLinkElement(url: string, params: StringMap = {}, as: string, crossOrigin?: string, type?: string): HTMLLinkElement {
    const newLink: HTMLLinkElement = document.createElement("link");
    if (Object.keys(params).length) {
        newLink.href = `${url}?${objectToQueryString(params)}`;
    } else {
        newLink.href = `${url}`;
    }
    if (crossOrigin) {
        newLink.crossOrigin = crossOrigin;
    }
    if (type) {
        newLink.type = type;
    }
    newLink.rel = "preload";
    newLink.as = as;
    return newLink;
}


/**
 * Clears the link cache. For testing purposes only.
 */
export function clearLinkCache(): void {
    preloadedLinks = {};
}
